import { useEffect, useRef } from "react";

const toLatLng = (point) => {
  if (!point || point.latitude == null || point.longitude == null) {
    return null;
  }
  return { lat: Number(point.latitude), lng: Number(point.longitude) };
};

const trailPointToLatLng = (point) => {
  if (!point) return null;
  const lat = point.lat ?? point.latitude;
  const lng = point.lng ?? point.longitude;
  if (lat == null || lng == null) return null;
  const parsed = { lat: Number(lat), lng: Number(lng) };
  if (Number.isNaN(parsed.lat) || Number.isNaN(parsed.lng)) return null;
  return parsed;
};

const createPinIcon = (label, fillColor, strokeColor = "#1a1a1a") => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
    <path d="M22 1C11.5 1 3 9.8 3 20.2c0 13.5 19 34.8 19 34.8s19-21.3 19-34.8C41 9.8 32.5 1 22 1z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="1.5"/>
    <circle cx="22" cy="20" r="12" fill="#ffffff"/>
    <text x="22" y="25" text-anchor="middle" font-family="system-ui,Arial,sans-serif" font-size="15" font-weight="700" fill="${strokeColor}">${label}</text>
  </svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(44, 56),
    anchor: new window.google.maps.Point(22, 56),
  };
};

const PICKUP_PIN = () => createPinIcon("P", "#22c55e", "#15803d");
const DROP_PIN = () => createPinIcon("D", "#1a1a1a", "#000000");

export default function RideTrackingMap({
  pickup,
  drop,
  driverLocation,
  locationTrail = [],
  showDriver = false,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const directionsRendererRef = useRef(null);
  const trailLineRef = useRef(null);
  const driverPulseRef = useRef(null);
  const routeKeyRef = useRef(null);

  useEffect(() => {
    if (!window.google?.maps || !mapRef.current) return;

    const pickupPos = toLatLng(pickup);
    const dropPos = toLatLng(drop);
    const center = pickupPos || dropPos || { lat: 30.7333, lng: 76.7794 };

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 13,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
    }

    const map = mapInstanceRef.current;

    const setMarker = (key, position, options) => {
      if (!position) {
        if (markersRef.current[key]) {
          markersRef.current[key].setMap(null);
          delete markersRef.current[key];
        }
        return;
      }
      if (markersRef.current[key]) {
        markersRef.current[key].setPosition(position);
        if (options?.title) {
          markersRef.current[key].setTitle(options.title);
        }
        return;
      }
      markersRef.current[key] = new window.google.maps.Marker({
        map,
        position,
        ...options,
      });
    };

    setMarker("pickup", pickupPos, {
      title: pickup?.address || "Pickup",
      icon: PICKUP_PIN(),
      zIndex: 200,
    });

    setMarker("drop", dropPos, {
      title: drop?.address || "Drop",
      icon: DROP_PIN(),
      zIndex: 201,
    });

    const trailPath = (Array.isArray(locationTrail) ? locationTrail : [])
      .map(trailPointToLatLng)
      .filter(Boolean);

    // Prefer actual GPS trail (driver's taken path) over planned Google Directions.
    if (trailPath.length >= 2) {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
        routeKeyRef.current = null;
      }

      if (!trailLineRef.current) {
        trailLineRef.current = new window.google.maps.Polyline({
          geodesic: true,
          strokeColor: "#16a34a",
          strokeOpacity: 0.95,
          strokeWeight: 5,
          zIndex: 2,
        });
      }
      trailLineRef.current.setPath(trailPath);
      trailLineRef.current.setMap(map);

      const bounds = new window.google.maps.LatLngBounds();
      trailPath.forEach((p) => bounds.extend(p));
      [pickupPos, dropPos].filter(Boolean).forEach((p) => bounds.extend(p));
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48);
      }
      return;
    }

    if (trailLineRef.current) {
      trailLineRef.current.setMap(null);
    }

    if (!pickupPos || !dropPos) {
      const bounds = new window.google.maps.LatLngBounds();
      [pickupPos, dropPos].filter(Boolean).forEach((p) => bounds.extend(p));
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48);
      }
      return;
    }

    const routeKey = `${pickupPos.lat},${pickupPos.lng}|${dropPos.lat},${dropPos.lng}`;
    if (routeKeyRef.current === routeKey) return;
    routeKeyRef.current = routeKey;

    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: "#2563eb",
          strokeOpacity: 0.85,
          strokeWeight: 4,
        },
      });
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: pickupPos,
        destination: dropPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status !== window.google.maps.DirectionsStatus.OK || !result) return;
        if (routeKeyRef.current !== routeKey) return;
        directionsRendererRef.current.setDirections(result);
        const bounds = result.routes?.[0]?.bounds;
        if (bounds) {
          map.fitBounds(bounds, 48);
        }
      },
    );
  }, [pickup, drop, locationTrail]);

  useEffect(() => {
    if (!window.google?.maps || !mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const driverPos = toLatLng(driverLocation);

    const setMarker = (key, position, options) => {
      if (!position) {
        if (markersRef.current[key]) {
          markersRef.current[key].setMap(null);
          delete markersRef.current[key];
        }
        return;
      }
      if (markersRef.current[key]) {
        markersRef.current[key].setPosition(position);
        return;
      }
      markersRef.current[key] = new window.google.maps.Marker({
        map,
        position,
        ...options,
      });
    };

    if (showDriver && driverPos) {
      setMarker("driver", driverPos, {
        title: "Driver",
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: "#ffc100",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        zIndex: 999,
      });

      if (!driverPulseRef.current) {
        driverPulseRef.current = new window.google.maps.Marker({
          map,
          position: driverPos,
          clickable: false,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 18,
            fillColor: "#ffc100",
            fillOpacity: 0.25,
            strokeColor: "#ffc100",
            strokeOpacity: 0.5,
            strokeWeight: 1,
          },
          zIndex: 998,
        });
      } else {
        driverPulseRef.current.setPosition(driverPos);
        driverPulseRef.current.setMap(map);
      }
    } else {
      if (markersRef.current.driver) {
        markersRef.current.driver.setMap(null);
        delete markersRef.current.driver;
      }
      if (driverPulseRef.current) {
        driverPulseRef.current.setMap(null);
        driverPulseRef.current = null;
      }
    }
  }, [driverLocation, showDriver]);

  useEffect(() => {
    return () => {
      Object.values(markersRef.current).forEach((m) => m?.setMap(null));
      directionsRendererRef.current?.setMap(null);
      trailLineRef.current?.setMap(null);
      driverPulseRef.current?.setMap(null);
    };
  }, []);

  return <div ref={mapRef} className="ride-tracking-map" />;
}
